import { useEffect, useState } from 'react';
import { Check, ChevronDown, Cloud, CloudOff, ExternalLink, Layers, RefreshCw } from 'lucide-react';
import api from '@/lib/api';
import { cn } from '@/lib/utils';
import { formatDate, formatDateTime } from '@/lib/format';
import { getErrorMessage } from '@/lib/errors';
import { Button } from '@/components/ui/button';
import { NotConfigured } from '@/components/ui/not-configured';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { formatFileSize, plural } from './documentsModel';
import { Avatar, IconTile } from './ui';

/** Source filter values (?source=): both, uploaded only, OneDrive only (16 idea 10). */
export const SOURCES = {
  all: 'Uploaded + OneDrive',
  uploaded: 'Uploaded',
  onedrive: 'OneDrive',
};
const COLLAPSED = 3;
const PAGE = 200;
/** While "Index now" runs on the server (in the background), the list is asked again this often. */
export const INDEXING_POLL_MS = 5000;
const EMPTY_INDEX = { items: [], total: 0, indexedAt: '', indexing: false, lastRun: null, loading: false, error: '' };

/**
 * The OneDrive index (GET /documents/onedrive-files) for the register — only asked for when the `onedrive`
 * integration is on; the search and the Project filter go to the server (debounced by the caller).
 */
export function useOneDriveIndex({ enabled, query, projectId }) {
  const [state, setState] = useState(EMPTY_INDEX);
  const [reloadKey, setReloadKey] = useState(0);
  useEffect(() => {
    if (!enabled) {
      setState(EMPTY_INDEX);
      return undefined;
    }
    let cancelled = false;
    setState((current) => ({ ...current, loading: true, error: '' }));
    const params = { limit: PAGE };
    if (query) params.search = query.slice(0, 100);
    if (projectId) params.project_id = projectId;
    api.get('/documents/onedrive-files', { params })
      .then((response) => {
        if (cancelled) return;
        const data = response.data || {};
        setState({
          items: Array.isArray(data.items) ? data.items : [],
          total: Number(data.total) || 0,
          indexedAt: data.indexed_at || '',
          indexing: Boolean(data.indexing),
          lastRun: data.last_run || null,
          loading: false,
          error: '',
        });
      })
      .catch((err) => {
        if (!cancelled) setState({ ...EMPTY_INDEX, error: getErrorMessage(err, 'Could not load the OneDrive files') });
      });
    return () => { cancelled = true; };
  }, [enabled, query, projectId, reloadKey]);
  // An index run in progress (Index now, or the nightly job): look again until it is done.
  useEffect(() => {
    if (!enabled || !state.indexing || state.loading) return undefined;
    const timer = setTimeout(() => setReloadKey((key) => key + 1), INDEXING_POLL_MS);
    return () => clearTimeout(timer);
  }, [enabled, state.indexing, state.loading]);
  return { ...state, reload: () => setReloadKey((key) => key + 1) };
}

/** "Source: Uploaded + OneDrive ⌄" — or "Source: Uploaded" with the not-set-up note while OneDrive is off. */
export function SourceChip({ value, onChange, feature, counts }) {
  const [open, setOpen] = useState(false);
  const on = feature.enabled;
  const current = on ? value : 'uploaded';
  const Icon = on ? Cloud : CloudOff;
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            'inline-flex h-9 shrink-0 items-center gap-1.5 rounded-full border border-line bg-surface px-3 text-[13px] font-semibold text-ink-2 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
            on && value !== 'all' && 'border-[color:var(--brand-soft-border)] bg-brand-soft text-brand-ink'
          )}
          data-testid="doc-filter-source"
          data-feature-status={feature.status}
        >
          <Icon className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" />
          Source: <span className="font-bold text-ink">{SOURCES[current]}</span>
          {on && counts ? (
            <span className="font-semibold tabular-nums text-ink-3">{counts.uploaded} · {counts.onedrive}</span>
          ) : null}
          <ChevronDown className="h-3.5 w-3.5 text-ink-3" aria-hidden="true" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[300px] rounded-xl border-line bg-surface p-2 shadow-overlay">
        <div role="listbox" aria-label="Source" className="space-y-0.5">
          {Object.entries(SOURCES).map(([key, label]) => {
            const disabled = !on && key !== 'uploaded';
            const selected = key === current;
            return (
              <button
                key={key}
                type="button"
                role="option"
                aria-selected={selected}
                aria-disabled={disabled || undefined}
                disabled={disabled}
                onClick={() => { onChange(key); setOpen(false); }}
                className={cn(
                  'flex w-full items-center gap-2 rounded-lg px-2.5 py-2 text-left text-[13px] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  disabled ? 'cursor-not-allowed text-ink-4' : selected ? 'bg-brand-soft text-ink' : 'text-ink-2 hover:bg-surface-hover'
                )}
                data-testid={`doc-source-${key}`}
              >
                <Check className={cn('h-3.5 w-3.5 shrink-0', selected ? 'text-brand-ink' : 'opacity-0')} aria-hidden="true" />
                <span className="flex-1 font-semibold">{label}</span>
                {on && counts && key !== 'all' ? <span className="text-[12px] tabular-nums text-ink-3">{counts[key]}</span> : null}
                {disabled ? <span className="text-[11.5px]">Not set up</span> : null}
              </button>
            );
          })}
        </div>
        {!on ? (
          <div className="mt-2 border-t border-line-2 px-1 pt-2">
            <NotConfigured
              feature="onedrive"
              layout="inline"
              text="When IT connects OneDrive, the files in every project's OneDrive folder are indexed here every night, with links to open them."
              testId="onedrive-not-configured"
            />
          </div>
        ) : null}
      </PopoverContent>
    </Popover>
  );
}

/** The register's column widths (DocumentsPage colgroup), so OneDrive rows line up under the documents on desktop. */
const ROW_GRID = 'lg:grid-cols-[minmax(0,1fr)_166px_156px_164px_92px_152px_96px]';
const cell = 'hidden min-w-0 px-3 lg:block';

function OneDriveRow({ file }) {
  const sub = [file.project_client, file.project_location].filter(Boolean).join(' · ');
  const link = file.web_url ? (
    <a
      href={file.web_url}
      target="_blank"
      rel="noopener noreferrer"
      className="inline-flex items-center gap-1 whitespace-nowrap rounded text-[12.5px] font-semibold text-brand-ink hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
    >
      Open in OneDrive
      <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
    </a>
  ) : <span className="text-[12px] text-ink-3">No link</span>;
  return (
    <li className={cn('grid grid-cols-[minmax(0,1fr)_auto] items-center gap-y-1 py-2.5', ROW_GRID)} data-testid={`onedrive-file-${file.id}`}>
      <div className="flex min-w-0 items-center gap-3 pl-4 pr-3">
        <IconTile icon={Cloud} tone="blue" size={32} title="OneDrive" />
        <div className="min-w-0">
          <span className="block truncate text-[13.5px] font-semibold text-ink" title={file.name}>{file.name}</span>
          <span className="block truncate text-[12px] text-ink-3" title={file.path}>
            OneDrive · {file.folder || 'project folder'}{file.size ? ` · ${formatFileSize(file.size)}` : ''}
          </span>
        </div>
      </div>
      <div className={cell}>
        <span className="block truncate text-[13px] font-semibold text-brand-ink">{file.project_name || 'Unknown project'}</span>
        {sub ? <span className="block truncate text-[11.5px] text-ink-3">{sub}</span> : null}
      </div>
      <div className={cell}>
        {file.stage_name ? (
          <span className="inline-flex h-6 max-w-full items-center gap-1 rounded-md border border-dashed border-line-2 px-2 text-[12px] font-medium text-ink-3" title="From the OneDrive folder name">
            <Layers className="h-3 w-3 shrink-0" aria-hidden="true" />
            <span className="truncate">{file.stage_name}</span>
          </span>
        ) : <span className="text-ink-4" aria-label="No stage">—</span>}
      </div>
      <div className="flex min-w-0 justify-end pr-4 lg:block lg:px-3">{link}</div>
      <div className={cell}><span className="text-ink-4" aria-label="No version">—</span></div>
      <div className={cell}>
        <span className="block text-[13px] text-ink">{formatDate(file.modified_at)}</span>
        {file.modified_by ? (
          <span className="mt-0.5 flex min-w-0 items-center gap-1.5 text-[12px] text-ink-3">
            <Avatar name={file.modified_by} size={18} />
            <span className="truncate">{file.modified_by}</span>
          </span>
        ) : null}
      </div>
      <div className={cell} />
    </li>
  );
}

/**
 * "From OneDrive": the indexed project files that match the search and the Project filter, under the uploaded
 * documents (the first 3, then "N more indexed files · Show"), or alone when Source is OneDrive.
 */
export function OneDriveSection({ index, alone, isAdmin, onIndexNow }) {
  const [expanded, setExpanded] = useState(false);
  const [starting, setStarting] = useState(false);
  const indexing = starting || Boolean(index.indexing);
  const showAll = alone || expanded;
  const items = showAll ? index.items : index.items.slice(0, COLLAPSED);
  const more = Math.max(0, index.total - items.length);
  const indexNow = async () => {
    setStarting(true);
    try {
      await onIndexNow();
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="border-t border-line" data-testid="onedrive-section" aria-label="Files from OneDrive">
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 bg-surface-2 px-4 py-2 text-[12.5px] text-ink-3">
        <span className="inline-flex items-center gap-1.5 font-bold text-ink">
          <Cloud className="h-4 w-4 text-brand-ink" aria-hidden="true" />
          From OneDrive
        </span>
        <span data-testid="onedrive-count">{plural(index.total, 'indexed file')}</span>
        {index.indexing ? (
          <span className="font-semibold text-brand-ink" data-testid="onedrive-indexing">· indexing now — the list updates when it is done</span>
        ) : index.indexedAt ? <span>· indexed {formatDateTime(index.indexedAt, { relativeDay: true })}</span> : <span>· not indexed yet</span>}
        <span className="ml-auto flex items-center gap-2">
          {isAdmin ? (
            <Button type="button" size="xs" variant="ghost" className="text-ink-3" onClick={indexNow} disabled={indexing} data-testid="onedrive-index-now">
              <RefreshCw className={cn(indexing && 'animate-spin')} aria-hidden="true" />
              {index.indexing ? 'Indexing…' : 'Index now'}
            </Button>
          ) : null}
        </span>
      </div>
      {index.error ? (
        <p className="px-4 py-3 text-[12.5px] text-status-red-ink" role="alert">{index.error}</p>
      ) : index.loading && !index.items.length ? (
        <p className="px-4 py-3 text-[12.5px] text-ink-3">Loading OneDrive files…</p>
      ) : !index.items.length ? (
        <p className="px-4 py-3 text-[12.5px] text-ink-3">
          {index.total === 0 && !index.indexedAt ? 'No project folder has been indexed yet — the nightly index runs at 02:30.' : 'No OneDrive files match.'}
        </p>
      ) : (
        <ul className="divide-y divide-line-2">
          {items.map((file) => <OneDriveRow key={file.id} file={file} />)}
        </ul>
      )}
      {!alone && index.items.length > COLLAPSED ? (
        <div className="flex justify-end border-t border-line-2 px-4 py-2 text-[12.5px]">
          <button type="button" onClick={() => setExpanded((value) => !value)} className="font-semibold text-brand-ink hover:underline" data-testid="onedrive-toggle">
            {expanded ? 'Show fewer' : `${plural(more, 'more indexed file')} · Show`}
          </button>
        </div>
      ) : null}
    </div>
  );
}
